import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LaunchScreen } from "@/components/loading/LaunchScreen";
import { useAuth } from "@/contexts/AuthContext";
import { clearBetaAccessCaches, forceBetaAcknowledgmentReauth, getBetaAcknowledgmentStatus, hardReloadToBetaGate, hasForcedBetaAcknowledgmentReauth } from "@/lib/betaAcknowledgmentGate";
import { logger } from "@/lib/logger";
import { readValidAccessCacheAsync, logOfflineBootstrapPath } from "@/lib/offlineAccessCache";

const PROTECTED_PREFIXES = [
  "/dashboard",
  "/select-plan",
  "/medications",
  "/appointments",
  "/history",
  "/reports",
  "/settings",
  "/care-team",
  "/export",
  "/install",
  "/google-calendar-callback",
  "/google-calendar-diagnostics",
  "/subscription",
  "/sales-download",
  "/crop-images",
  "/email-collection",
  "/admin",
  "/newsletter-admin",
  "/beta-email-approval",
  "/security-dashboard",
  "/upload-email-logo",
  "/generate-logo-base64",
  "/marketing",
];


export const BetaAcknowledgmentRouteGate = ({ children }: { children: React.ReactNode }) => {
  const { user, session, loading, isAuthReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [allowedGateKey, setAllowedGateKey] = useState<string | null>(null);

  const shouldGatePath = useMemo(() => {
    if (!user?.id) return false;
    if (location.pathname === "/") return false;
    return PROTECTED_PREFIXES.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
  }, [location.pathname, user?.id]);

  const gateKey = shouldGatePath && user?.id ? `${user.id}:${location.pathname}` : null;

  useEffect(() => {
    let cancelled = false;

    const runGate = async () => {
      if (!isAuthReady || loading || !session?.user || !user?.id || !shouldGatePath) {
        setAllowedGateKey(null);
        return;
      }

      if (!navigator.onLine) {
        const hasValidOfflineAccessCache = !!(await readValidAccessCacheAsync(user.id));
        if (cancelled) return;

        if (hasValidOfflineAccessCache) {
          logger.log("[BetaAcknowledgmentRouteGate] Offline with verified access cache; allowing cached route");
          logOfflineBootstrapPath("offline_cache_hit", { source: "beta_gate" });
          setAllowedGateKey(`${user.id}:${location.pathname}`);
        } else {
          logger.warn("[BetaAcknowledgmentRouteGate] Offline without verified access cache; blocking protected route");
          logOfflineBootstrapPath("offline_cache_miss", { source: "beta_gate" });
          setAllowedGateKey(null);
        }
        return;
      }

      const status = await getBetaAcknowledgmentStatus(user.id);
      if (cancelled) return;

      if (status.gateCheckError) {
        logger.warn("[BetaAcknowledgmentRouteGate] Gate check failed; blocking protected route", status.gateCheckError.message);
        return;
      }

      if (status.isUnsignedBetaTester) {
        clearBetaAccessCaches(user.id);
        if (!hasForcedBetaAcknowledgmentReauth(user.id)) {
          await forceBetaAcknowledgmentReauth(user.id);
          if (!cancelled) await hardReloadToBetaGate("/auth?beta_ack=required");
          return;
        }
        if (!cancelled) await hardReloadToBetaGate("/beta-acknowledgment");
        return;
      }

      setAllowedGateKey(`${user.id}:${location.pathname}`);
    };

    void runGate();

    return () => {
      cancelled = true;
    };
  }, [isAuthReady, loading, location.pathname, navigate, session?.user, shouldGatePath, user?.id]);

  if (gateKey && allowedGateKey !== gateKey) {
    return <LaunchScreen />;
  }

  return <>{children}</>;
};